import { NotificationsActive } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import { useMemo, useState } from 'react'

import { useCreateScoutAssignmentMutation } from '../../../api/appointments.queries'

import { useScoutsQuery } from '@/features/scouts/api'
import type { Scout } from '@/features/scouts/types'

// Constants
const SCOUTS_LIMIT = 100

interface ManualNotifyDialogProps {
  open: boolean
  onClose: () => void
  appointmentId: string
  appointmentDetails?: {
    customerName?: string
    customerAddress?: string
  }
}

export const ManualNotifyDialog = ({
  open,
  onClose,
  appointmentId,
  appointmentDetails,
}: ManualNotifyDialogProps) => {
  const [selectedScout, setSelectedScout] = useState<Scout | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')

  // API: Fetch scouts with search query when dialog is open
  const {
    data: scoutsData,
    isLoading: scoutsLoading,
    error: scoutsError,
  } = useScoutsQuery(
    {
      limit: SCOUTS_LIMIT,
      q: searchQuery || undefined,
      status: 'Aktiv', // Only show active scouts
    },
    { enabled: open }
  )

  // API: Create scout assignment mutation
  const createAssignmentMutation = useCreateScoutAssignmentMutation()

  // Data: Memoized scouts list
  const scouts = useMemo((): Scout[] => (scoutsData?.data as Scout[]) || [], [scoutsData])

  // Handlers
  const handleSubmit = async () => {
    if (selectedScout && appointmentId) {
      try {
        await createAssignmentMutation.mutateAsync({
          appointmentId,
          scoutUUID: selectedScout.uuid,
        })
        // Close dialog on success - the mutation hook will handle query invalidation
        handleClose()
      } catch (error) {
        console.error('Failed to create scout assignment:', error)
      }
    }
  }

  const handleClose = () => {
    // Reset all form state
    setSelectedScout(null)
    setSearchQuery('')
    onClose()
  }

  // Form state
  const isSubmitDisabled = !selectedScout || !appointmentId || createAssignmentMutation.isPending

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsActive color="primary" />
        Manually Notify Scout
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Info Alert */}
          {appointmentDetails && (
            <Alert severity="info">
              This will manually create a scout assignment and send a notification to the selected
              scout for the appointment
              {appointmentDetails.customerName && ` with ${appointmentDetails.customerName}`}
              {appointmentDetails.customerAddress && ` at ${appointmentDetails.customerAddress}`}.
            </Alert>
          )}

          {/* Scout Selection */}
          <Autocomplete
            value={selectedScout}
            onChange={(_, newValue) => setSelectedScout(newValue)}
            options={scouts}
            loading={scoutsLoading}
            disabled={createAssignmentMutation.isPending}
            inputValue={searchQuery}
            onInputChange={(_, newValue) => setSearchQuery(newValue)}
            getOptionLabel={(option: Scout) =>
              `${option.firstName} ${option.lastName} (${option.email})`
            }
            isOptionEqualToValue={(option: Scout, value: Scout) => option.uuid === value.uuid}
            renderInput={params => (
              <TextField
                {...params}
                label="Select Scout"
                placeholder="Search by name or email..."
                error={!!scoutsError}
                helperText={scoutsError ? 'Failed to load scouts' : undefined}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {scoutsLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option: Scout) => (
              <li {...props} key={option.uuid}>
                <Stack>
                  <span>
                    {option.firstName} {option.lastName}
                  </span>
                  <span style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    {option.email} • {option.phoneNumber}
                  </span>
                </Stack>
              </li>
            )}
          />

          {/* Error Alert */}
          {createAssignmentMutation.isError && (
            <Alert severity="error">
              Failed to create scout assignment. Please try again.
              {createAssignmentMutation.error?.message &&
                `: ${createAssignmentMutation.error.message}`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createAssignmentMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitDisabled}
          startIcon={
            createAssignmentMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              <NotificationsActive />
            )
          }
        >
          {createAssignmentMutation.isPending ? 'Creating Assignment...' : 'Notify Scout'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
